/**
 * ProviderBadge - labelled badge identifying which ICP lab produced a test.
 *
 * Always renders label + color together (never color alone) so provider
 * identity is accessible without relying on color perception.
 */
import { Badge, type BadgeProps } from '@mantine/core';
import { PROVIDER_LABELS } from '@/utils/icpProviderDetect';
import type { ICPProvider } from '@/types/sensor';

// Theme palette colors per provider:
// - teal: the app's primary color (Reef Zelements is the original provider)
// - blue / orange: standard Mantine theme colors already used as category
//   colors elsewhere (coral stays reserved for warning/out-of-range states)
const PROVIDER_COLORS: Record<ICPProvider, string> = {
  reef_zelements: 'teal',
  ati: 'blue',
  triton: 'orange',
};

export interface ProviderBadgeProps {
  /**
   * Provider id from the API (e.g. 'reef_zelements'). Unknown, empty, or
   * missing values fall back to a gray "Unknown" badge rather than rendering
   * a blank or literal "undefined" label.
   */
  provider: string | null | undefined;
  size?: BadgeProps['size'];
}

function isKnownProvider(provider: string): provider is ICPProvider {
  return provider in PROVIDER_COLORS;
}

export function ProviderBadge({ provider, size = 'sm' }: ProviderBadgeProps) {
  const value = (provider ?? '').trim();
  const known = isKnownProvider(value);
  const color = known ? PROVIDER_COLORS[value] : 'gray';
  const label = known ? PROVIDER_LABELS[value] : value || 'Unknown';

  return (
    <Badge
      variant="light"
      color={color}
      size={size}
      tt="uppercase"
      style={{ fontSize: 10, letterSpacing: '0.05em', fontWeight: 600 }}
    >
      {label}
    </Badge>
  );
}
